import { ColumnElement, ColumnTitle } from '@atoms/columnElement';
import { DateFormatPicker } from '@atoms/dateFormat/dateFormat';
import { ExempleTooltip, InfoTooltip } from '@atoms/infoTooltip';
import { SwitchV2 } from '@atoms/switch';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { ColumnComp } from '@components/trace/column/column.comp';
import { Label } from '@components/ui/label';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import {
  type ColumnData,
  type DNDBlock,
  ResolvedVariableType
} from '@stratumn/studio-shared';
import { DateWidget } from '@widgets/date/date.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialDateData } from './date.data';
import { DateSettingsModal } from './date.settings';
import { DateIcon } from './date.utils';

const DateTableImporterBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Importer_Date',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_importer',
    initialData: initialDateData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Date
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Date Column Title',
    content: <DateSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  const onTitleChange = (title: ColumnData['title']) => {
    if (atom.data.title === atom.data.importerOptions.from) {
      atom.data.importerOptions.from = title; // From follows the column header
    }
    atom.data.title = title;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={DateIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={onTitleChange}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <Column $width="220px">
        <ColumnTitle>CSV parser options</ColumnTitle>
        <ColumnElement>
          <SwitchV2
            checked={atom.data.importerOptions.parserOptions.activated}
            id={`parser-activated-${atom.id}`}
            labelText="Parse CSV"
            onCheckedChange={v =>
              (atom.data.importerOptions.parserOptions.activated = v)
            }
          />
        </ColumnElement>
        {atom.data.importerOptions.parserOptions.activated === true && (
          <>
            <ColumnElement>
              <div className="flex items-center gap-2">
                <Label htmlFor={`parser-input-format-${atom.id}`}>
                  CSV date format
                </Label>
                <InfoTooltip
                  additionalContent={
                    <ExempleTooltip
                      exampleName="Input format"
                      input="DD/MM/YYYY"
                      output="21/01/2023"
                    />
                  }
                  description={`The format of the dates in the column ${atom.data.importerOptions.from} of the CSV file`}
                  title="CSV date format"
                />
              </div>
              <DateFormatPicker
                format={
                  atom.data.importerOptions.parserOptions.parser.inputFormat
                }
                id={`parser-input-format-${atom.id}`}
                onFormatChange={format =>
                  (atom.data.importerOptions.parserOptions.parser.inputFormat =
                    format)
                }
              />
            </ColumnElement>
            <ColumnElement>
              <div className="flex items-center gap-2">
                <Label htmlFor={`parser-output-format-${atom.id}`}>
                  Stored date format
                </Label>
                <InfoTooltip
                  additionalContent={
                    <ExempleTooltip
                      exampleName="Output format"
                      input="DD/MM/YYYY"
                      output="21/01/2023"
                    />
                  }
                  description="The format in which the imported date is stored in the Trace's state"
                  title="Stored date format"
                />
              </div>
              <DateFormatPicker
                format={
                  atom.data.importerOptions.parserOptions.parser.outputFormat
                }
                id={`parser-output-format-${atom.id}`}
                onFormatChange={format =>
                  (atom.data.importerOptions.parserOptions.parser.outputFormat =
                    format)
                }
              />
            </ColumnElement>
          </>
        )}
      </Column>
      <DateWidget
        atomId={atom.id}
        format={atom.data.cell.view.format}
        onFormatChange={format => (atom.data.cell.view.format = format)}
      />
    </BlockBase>
  );
};

export default observer(DateTableImporterBlock);
